import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { notificationsService } from '../services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Activity, 
  Loader2, 
  LogIn, 
  LogOut, 
  UserPlus, 
  UserX,
  UserCheck,
  Shield,
  Key,
  Calendar,
  CheckCircle,
  XCircle,
  RefreshCw,
  Mail,
  Clock,
  Send,
  AlertCircle,
  Users
} from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import api from '../services/api';

const actionConfig = {
  LOGIN: { icon: LogIn, label: 'Login', color: 'bg-green-100 text-green-800' },
  LOGOUT: { icon: LogOut, label: 'Logout', color: 'bg-slate-100 text-slate-800' },
  USER_CREATE: { icon: UserPlus, label: 'User Created', color: 'bg-blue-100 text-blue-800' },
  USER_DEACTIVATE: { icon: UserX, label: 'User Deactivated', color: 'bg-red-100 text-red-800' },
  USER_ACTIVATE: { icon: UserCheck, label: 'User Activated', color: 'bg-green-100 text-green-800' },
  USER_ROLE_CHANGE: { icon: Shield, label: 'Role Changed', color: 'bg-purple-100 text-purple-800' },
  USER_UPDATE: { icon: Shield, label: 'User Updated', color: 'bg-blue-100 text-blue-800' },
  PASSWORD_RESET: { icon: Key, label: 'Password Reset', color: 'bg-amber-100 text-amber-800' },
  STATUS_CHANGE: { icon: Calendar, label: 'Schedule Status', color: 'bg-indigo-100 text-indigo-800' }
};

const notificationTypeConfig = {
  publish: { label: 'Publish', color: 'bg-blue-100 text-blue-800' },
  update: { label: 'Update', color: 'bg-purple-100 text-purple-800' },
  cancellation: { label: 'Cancellation', color: 'bg-red-100 text-red-800' }
};

const notificationStatusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800' }
};

export default function ActivityLogPage() {
  const { isAdmin, hasAnyRole } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('activity');
  
  // Activity Log State
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Notification Log State
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationTotal, setNotificationTotal] = useState(0);

  const canViewNotifications = hasAnyRole(['admin', 'senior_scheduler', 'scheduler']);

  const loadUsers = useCallback(async () => {
    try {
      const response = await api.get('/users?include_inactive=true');
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (userFilter) params.append('user_id', userFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/audit/activity?${params.toString()}`);
      setLogs(response.data);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load activity log',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast, userFilter, startDate, endDate]);

  const loadNotificationLogs = useCallback(async () => {
    if (!canViewNotifications) return;
    
    try {
      setNotificationLoading(true);
      const response = await notificationsService.getLogs({ limit: 50 });
      setNotificationLogs(response.logs || []);
      setNotificationTotal(response.total || 0);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load notification logs',
        variant: 'destructive'
      });
    } finally {
      setNotificationLoading(false);
    }
  }, [toast, canViewNotifications]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (activeTab === 'activity') {
      loadLogs();
    } else if (activeTab === 'notifications') {
      loadNotificationLogs();
    }
  }, [activeTab, loadLogs, loadNotificationLogs]);

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : `User #${userId}`;
  };

  const getActionDetails = (log) => {
    const config = actionConfig[log.action] || { icon: Activity, label: log.action, color: 'bg-gray-100 text-gray-800' };
    const Icon = config.icon;
    
    let details = '';
    const afterData = log.after_data || {};
    const beforeData = log.before_data || {};
    
    switch (log.action) {
      case 'LOGIN':
        details = `Logged in from ${log.ip_address || 'unknown IP'}`;
        break;
      case 'LOGOUT':
        details = `Logged out`;
        break;
      case 'USER_CREATE':
        details = `Created user: ${afterData.email || 'unknown'} (${afterData.role || 'unknown role'})`;
        break;
      case 'USER_DEACTIVATE':
        details = `Deactivated user: ${afterData.email || 'unknown'}`;
        break;
      case 'USER_ACTIVATE':
        details = `Activated user: ${afterData.email || 'unknown'}`;
        break;
      case 'USER_ROLE_CHANGE':
        details = `Changed role: ${afterData.email || 'unknown'} - ${beforeData.role || '?'} → ${afterData.role || '?'}`;
        break;
      case 'USER_UPDATE':
        details = `Updated user: ${afterData.email || 'unknown'}`;
        break;
      case 'PASSWORD_RESET':
        details = `Reset password for: ${afterData.email || 'unknown'}`;
        break;
      case 'STATUS_CHANGE':
        const oldStatus = beforeData.status || '?';
        const newStatus = afterData.status || '?';
        details = `Job #${log.entity_id}: ${oldStatus} → ${newStatus}`;
        break;
      default:
        details = log.action;
    }
    
    return { Icon, config, details };
  };

  if (!isAdmin && !canViewNotifications) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Admin access required</p>
      </div>
    );
  }

  return (
    <div data-testid="activity-log-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Activity Log
          </h1>
          <p className="text-muted-foreground">Track user activity and notification history</p>
        </div>
        <Button 
          onClick={activeTab === 'activity' ? loadLogs : loadNotificationLogs} 
          variant="outline" 
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="activity" data-testid="activity-tab">
            <Activity className="h-4 w-4 mr-2" />
            User Activity
          </TabsTrigger>
          {canViewNotifications && (
            <TabsTrigger value="notifications" data-testid="notifications-tab">
              <Mail className="h-4 w-4 mr-2" />
              Notification Log
            </TabsTrigger>
          )}
        </TabsList>

        {/* User Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground mb-1 block">User</Label>
                  <Select value={userFilter || 'all'} onValueChange={(v) => setUserFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger data-testid="user-filter">
                      <SelectValue placeholder="All Users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">From</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-[160px]"
                    data-testid="start-date-filter"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">To</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-[160px]"
                    data-testid="end-date-filter"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Log Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-16">
                  <Activity className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No activity found for this period</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">When</TableHead>
                      <TableHead className="w-[150px]">Who</TableHead>
                      <TableHead className="w-[140px]">Action</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead className="w-[120px]">IP Address</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map(log => {
                      const { Icon, config, details } = getActionDetails(log);
                      return (
                        <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                          <TableCell className="font-mono text-sm">
                            {format(parseISO(log.created_at), 'dd MMM yyyy HH:mm:ss')}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{getUserName(log.user_id)}</span>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${config.color} gap-1`}>
                              <Icon className="h-3 w-3" />
                              {config.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {details}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {log.ip_address || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          
          {/* Legend */}
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-2">Tracked Events:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(actionConfig).map(([action, config]) => {
                const Icon = config.icon;
                return (
                  <Badge key={action} variant="outline" className="gap-1">
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </Badge>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Notification Log Tab */}
        {canViewNotifications && (
          <TabsContent value="notifications" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Batches</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{notificationTotal}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Last Sent</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {notificationLogs.length > 0 && notificationLogs[0].completed_at
                      ? format(parseISO(notificationLogs[0].completed_at), 'dd MMM HH:mm')
                      : '-'}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {notificationLogs.filter(n => n.status === 'pending').length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Notification Log Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Notification History
                </CardTitle>
                <CardDescription>
                  Schedule notifications sent to auditors with calendar invites
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {notificationLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : notificationLogs.length === 0 ? (
                  <div className="text-center py-16">
                    <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">No notification batches yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Use "Publish Schedule" on the Schedule page to send notifications
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[160px]">Date/Time</TableHead>
                        <TableHead className="w-[100px]">Type</TableHead>
                        <TableHead className="w-[100px]">Mode</TableHead>
                        <TableHead>Date Range</TableHead>
                        <TableHead className="text-center w-[100px]">Auditors</TableHead>
                        <TableHead className="text-center w-[80px]">Jobs</TableHead>
                        <TableHead className="w-[120px]">Status</TableHead>
                        <TableHead className="w-[120px]">Results</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notificationLogs.map(batch => {
                        const typeConfig = notificationTypeConfig[batch.notification_type] || { label: batch.notification_type, color: 'bg-gray-100' };
                        const statusConfig = notificationStatusConfig[batch.status] || { label: batch.status, color: 'bg-gray-100' };
                        
                        return (
                          <TableRow key={batch.id} data-testid={`notification-row-${batch.id}`}>
                            <TableCell className="font-mono text-sm">
                              {batch.created_at 
                                ? format(parseISO(batch.created_at), 'dd MMM yyyy HH:mm')
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={typeConfig.color}>
                                {typeConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                {batch.send_mode === 'immediate' ? (
                                  <><Send className="h-3 w-3" /> Immediate</>
                                ) : (
                                  <><Clock className="h-3 w-3" /> 5 PM Batch</>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {batch.start_date && batch.end_date ? (
                                <>
                                  {format(parseISO(batch.start_date), 'dd MMM')} - {format(parseISO(batch.end_date), 'dd MMM yyyy')}
                                </>
                              ) : '-'}
                              {batch.region && (
                                <span className="ml-2 text-xs text-muted-foreground">({batch.region})</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Users className="h-3 w-3 text-muted-foreground" />
                                {batch.total_auditors}
                              </div>
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {batch.total_jobs}
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {batch.status === 'completed' ? (
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-green-600 flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    {batch.recipients_sent}
                                  </span>
                                  {batch.recipients_failed > 0 && (
                                    <span className="text-red-600 flex items-center gap-1">
                                      <XCircle className="h-3 w-3" />
                                      {batch.recipients_failed}
                                    </span>
                                  )}
                                </div>
                              ) : batch.status === 'pending' && batch.scheduled_send_at ? (
                                <span className="text-xs text-muted-foreground">
                                  Scheduled: {format(parseISO(batch.scheduled_send_at), 'HH:mm')}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-2">Notification Types:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(notificationTypeConfig).map(([type, config]) => (
                  <Badge key={type} variant="outline" className={config.color}>
                    {config.label}
                  </Badge>
                ))}
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
